import type { ErrorFrame } from '../../../ports/ErrorFrame';

export interface WireFrame {
  type: string;
  [key: string]: unknown;
}

export interface ConnectedFrame extends WireFrame {
  type: 'connected';
  client_bootstrap?: Record<string, unknown>;
  experience_session_bootstrap?: Record<string, unknown> & { status?: string };
}

export function isConnectedFrame(frame: WireFrame): frame is ConnectedFrame {
  return isWireFrame(frame) && frame.type === 'connected';
}

export interface WireActionTarget {
  actor_instance_id?: string;
  character_instance_id?: string;
  character_template_id?: string;
}

export interface WireActionContext {
  room_instance_id?: string;
  container_ref?: string;
  conversation_scope_ref?: string;
}

export interface WireActionFrame extends WireFrame {
  type: 'action';
  action: string;
  target?: WireActionTarget;
  context?: WireActionContext;
}

export function isWireActionFrame(frame: unknown): frame is WireActionFrame {
  return (
    isWireFrame(frame) &&
    frame.type === 'action' &&
    isNonEmptyString(frame.action) &&
    (frame.target === undefined || isWireActionTarget(frame.target)) &&
    (frame.context === undefined || isWireActionContext(frame.context))
  );
}

export interface ActionResponseFrame extends WireFrame {
  type: 'action_response';
  action: string;
  success: boolean;
  message: string;
  request_id?: string;
  projection?: {
    plain_text?: string;
    [key: string]: unknown;
  };
  state_changes?: unknown;
  metadata?: unknown;
}

export function isActionResponseFrame(frame: WireFrame): frame is ActionResponseFrame {
  return (
    isWireFrame(frame) &&
    frame.type === 'action_response' &&
    isNonEmptyString(frame.action) &&
    typeof frame.success === 'boolean' &&
    isNonEmptyString(frame.message) &&
    (frame.request_id === undefined || isNonEmptyString(frame.request_id)) &&
    (frame.projection === undefined || isRecord(frame.projection))
  );
}

export function isErrorFrame(frame: WireFrame): frame is ErrorFrame {
  return isWireFrame(frame) && frame.type === 'error' && isNonEmptyString(frame.message);
}

export interface MissionUpdateFrame extends WireFrame {
  type: 'mission_update';
  [key: string]: unknown;
}

export function isMissionUpdateFrame(frame: WireFrame): frame is MissionUpdateFrame {
  return isWireFrame(frame) && frame.type === 'mission_update';
}

export interface SimEventFrame extends WireFrame {
  type: 'sim_event';
  [key: string]: unknown;
}

export function isSimEventFrame(frame: WireFrame): frame is SimEventFrame {
  return isWireFrame(frame) && frame.type === 'sim_event';
}

export interface WorldUpdateFrame extends WireFrame {
  type: 'world_update';
  [key: string]: unknown;
}

export function isWorldUpdateFrame(frame: WireFrame): frame is WorldUpdateFrame {
  return isWireFrame(frame) && frame.type === 'world_update';
}

export interface AreaOfInterestFrame extends WireFrame {
  type: 'area_of_interest';
  [key: string]: unknown;
}

export function isAreaOfInterestFrame(frame: WireFrame): frame is AreaOfInterestFrame {
  return isWireFrame(frame) && frame.type === 'area_of_interest';
}

// Server-held utterance envelope. See
// docs/reference/api/streaming/server-held-utterance-contract.md in the gaia
// server repo: Gaia owns which utterance plays, its order, and whether it
// interrupts; the client renders the instruction it receives.
export interface WireUtteranceSpeaker {
  actor_instance_id?: string;
  actor_template_id?: string;
  role?: string;
}

export interface WireUtteranceDisposition {
  mode?: 'enqueue' | 'interrupt';
  interrupts_utterance_id?: string;
}

// The step's answer contract (Gaia PR #1030). Read loosely here; the adapter
// validates shape before trusting it and treats anything malformed as absent.
export interface WireUtteranceResponse {
  awaiting?: boolean;
  step_id?: string;
  accepts?: string[];
}

export interface WireUtteranceSource {
  kind?: string;
  mission_id?: string;
  step_id?: string;
  line_id?: string;
  response?: WireUtteranceResponse;
}

// A presentation directive, still in wire shape: {"m", "p", "offset_ms"?}.
// Read loosely here (the adapter validates each entry against the shared
// repertoire before trusting it, per presentationDirectives.ts).
export interface WirePresentationDirective {
  m?: string;
  p?: Record<string, unknown>;
  offset_ms?: number;
  /** "authored" or "model" (Gaia contract section 2). Informational only; the client ignores it. */
  source?: string;
  [key: string]: unknown;
}

export interface UtteranceFrame extends WireFrame {
  type: 'experience.utterance';
  session_id: string;
  utterance_id: string;
  sequence?: number;
  speaker: WireUtteranceSpeaker;
  text: string;
  priority?: 'ordinary' | 'important' | 'urgent';
  disposition?: WireUtteranceDisposition;
  source?: WireUtteranceSource;
  // Both fields are top-level on the utterance, siblings of `source` (Gaia
  // contract section 2), not nested inside it. Absent, not empty, when a
  // line has neither.
  presentation_directives?: WirePresentationDirective[];
  /** The current step's authored display name, for the card header's status word. Omitted, never a raw step id. */
  step_title?: string;
}

// The server's reply to experience.request_config, carrying the character id
// the session should speak as.
export interface ConfigFrame extends WireFrame {
  type: 'experience.config';
  session_id: string;
  characterId?: string;
  character_id?: string;
  published_version_id?: string;
  publishedVersionId?: string;
  experienceKey?: string;
  experience_key?: string;
  nodes?: unknown;
  missionPresentationVocabulary?: unknown;
}

export function isConfigFrame(frame: WireFrame): frame is ConfigFrame {
  return isWireFrame(frame) && frame.type === 'experience.config' && isNonEmptyString(frame.session_id);
}

export function isUtteranceFrame(frame: WireFrame): frame is UtteranceFrame {
  return (
    isWireFrame(frame) &&
    frame.type === 'experience.utterance' &&
    isNonEmptyString(frame.session_id) &&
    isNonEmptyString(frame.utterance_id) &&
    isNonEmptyString(frame.text) &&
    isRecord(frame.speaker)
  );
}

export function isWireFrame(value: unknown): value is WireFrame {
  return isRecord(value) && isNonEmptyString(value.type);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function isOptionalString(value: unknown): value is string | undefined {
  return value === undefined || typeof value === 'string';
}

const WIRE_ACTION_TARGET_KEYS = new Set([
  'actor_instance_id',
  'character_instance_id',
  'character_template_id',
]);

const WIRE_ACTION_CONTEXT_KEYS = new Set([
  'room_instance_id',
  'container_ref',
  'conversation_scope_ref',
]);

function hasOnlyKeys(value: Record<string, unknown>, allowedKeys: Set<string>): boolean {
  return Object.keys(value).every((key) => allowedKeys.has(key));
}

function isWireActionTarget(value: unknown): value is WireActionTarget {
  if (!isRecord(value) || !hasOnlyKeys(value, WIRE_ACTION_TARGET_KEYS)) return false;
  return (
    isOptionalString(value.actor_instance_id) &&
    isOptionalString(value.character_instance_id) &&
    isOptionalString(value.character_template_id)
  );
}

function isWireActionContext(value: unknown): value is WireActionContext {
  if (!isRecord(value) || !hasOnlyKeys(value, WIRE_ACTION_CONTEXT_KEYS)) return false;
  return (
    isOptionalString(value.room_instance_id) &&
    isOptionalString(value.container_ref) &&
    isOptionalString(value.conversation_scope_ref)
  );
}
